import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listEcheqs } from '@/shared/api/controllers/echeqs';
import {
  descartarAlertas,
  listEcheqsRebotados,
  listResoluciones,
  type EcheqRebotado,
  type Resolucion,
} from '@/shared/api/controllers/alertas';
import { textoCobro } from '@/features/administracion/components/EcheqsBandeja';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Bell,
  Check,
  ChevronDown,
  Filter,
  ListFilter,
  Loader2,
  ReceiptText,
  ScrollText,
  TrendingUp,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { useSession } from '@/shared/auth/SessionProvider';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import {
  listAllOperaciones,
  type ResumenOperacionesItem,
} from '@/shared/api/controllers/resumenOperaciones';
import {
  getSircrebParam,
  isParametroUpToDate,
  sircrebPercent,
} from '@/shared/api/controllers/parametros';
import { listAllParametrosComDesc } from '@/shared/api/controllers/parametrosComDesc';
import { listDescuentosByVendedorMes } from '@/shared/api/controllers/descuentos';
import {
  esCaeHuerfano,
  listFacturasAnticipadasPendientes,
  listFacturasArca,
  type FacturaArcaRow,
} from '@/features/facturacion/api';
import { hoyAr, plazoNc, textoPlazo, type PlazoNc } from '@/features/facturacion/plazoNc';
import { GenerarNotaCreditoDialog } from '@/features/ventas/components/GenerarNotaCreditoDialog';
import { solicitarAnulacionComprobante } from '@/shared/api/controllers/ventas';
import { ApiError } from '@/shared/api/client';
import {
  countMonthlyDescuentos,
  DISCOUNT_BRACKETS,
  readAllowanceLimits,
  readBracketPercents,
  type DiscountBracket,
} from '@/features/ventas/lib/discounts';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { SircrebUpdateDialog } from './SircrebUpdateDialog';
import { listPromesasCobro, type PromesaCobro } from '@/features/administracion/api';
import { diasHasta, estadoPromesa, POR_VENCER_DIAS, textoDias } from '@/features/administracion/lib/promesas';

/**
 * Home / Inicio — ports `Screen_Home.pa.yaml`. Shows four KPI cards across
 * the top (SIRCREB is admin-only, the other three count current-month rows
 * of `01.ResumenOperaciones`) and a grid of "operaciones activas" below
 * that the user can filter by Tipo / Status.
 *
 * The SIRCREB card doubles as the trigger for the update dialog, and the
 * dialog auto-opens once per session when the admin lands on Home with an
 * outdated SIRCREB row — matching the PowerApps `PopUp_PendienteSIRCREB`
 * behaviour on `OnVisible`.
 */

const STATUS_OPTIONS = [
  'Cotizado',
  'Pendiente de Pago',
  'Confirmacion de Pago',
  'Pago Parcial',
  'Pendiente Despacho',
  'Pendiente Puesta Marcha',
] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];

const DEFAULT_STATUSES: StatusOption[] = ['Cotizado', 'Confirmacion de Pago'];

const VENTAS_CONCRETADAS_STATUSES = new Set([
  'Pendiente Despacho',
  'Pendiente Puesta Marcha',
  'Cerrado',
]);

type TipoFilter = 'Todos' | 'Venta' | 'Cotizacion';

export function HomePage() {
  const navigate = useNavigate();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const isVentasRole = profile?.perfil === 'ventas';
  const canUpdateSircreb =
    activeCountry.features.has('sircreb') &&
    (profile?.perfil === 'admin' || profile?.perfil === 'administracion');

  const operacionesQ = useQuery({
    // Misma clave que la grilla de Ventas: una sola descarga de todas las
    // ventas sirve a las dos pantallas (y su Realtime la mantiene al día).
    queryKey: ['ventas', 'resumen-operaciones', countryCode],
    queryFn: listAllOperaciones,
    staleTime: 30_000,
  });
  const sircrebQ = useQuery({
    queryKey: ['home', countryCode, 'sircreb'],
    queryFn: getSircrebParam,
    enabled: canUpdateSircreb,
    staleTime: 60_000,
  });

  // Vendedor-only: monthly discount usage vs allowance per bracket. Mirrors
  // the counters that PowerApps pre-calculates at login (Descuentos5Porc /
  // Descuentos10Porc / Descuentos15Porc) and displays alongside the KPIs.
  const mesAnio = useMemo(() => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `${mm}/${now.getFullYear()}`;
  }, []);
  const vendedor = profile?.usuario_app ?? '';
  const descuentosMesQ = useQuery({
    queryKey: ['home', countryCode, 'descuentos-mes', vendedor, mesAnio],
    queryFn: () => listDescuentosByVendedorMes(vendedor, mesAnio),
    enabled: isVentasRole && Boolean(vendedor),
    staleTime: 60_000,
  });
  const parametrosComDescQ = useQuery({
    queryKey: ['home', countryCode, 'parametros-com-desc'],
    queryFn: listAllParametrosComDesc,
    enabled: isVentasRole,
    staleTime: 60_000,
  });
  const discountRemaining = useMemo<Record<DiscountBracket, number>>(() => {
    if (!isVentasRole) return { A: 0, B: 0, C: 0 };
    const limits = readAllowanceLimits(parametrosComDescQ.data ?? []);
    const used = countMonthlyDescuentos(descuentosMesQ.data ?? []);
    return {
      A: Math.max(0, limits.A - used.A),
      B: Math.max(0, limits.B - used.B),
      C: Math.max(0, limits.C - used.C),
    };
  }, [isVentasRole, parametrosComDescQ.data, descuentosMesQ.data]);
  const bracketPercents = useMemo(
    () =>
      isVentasRole
        ? readBracketPercents(parametrosComDescQ.data ?? [])
        : { A: 0, B: 0, C: 0 },
    [isVentasRole, parametrosComDescQ.data],
  );
  // Only show cards for configured brackets (percentage > 0).
  const configuredBrackets = DISCOUNT_BRACKETS.filter((b) => bracketPercents[b] > 0);
  const discountLoading = descuentosMesQ.isPending || parametrosComDescQ.isPending;

  // --- KPI counts -----------------------------------------------------
  const monthKey = mesAnio;

  const scoped = useMemo(() => {
    const all = operacionesQ.data ?? [];
    if (!isVentasRole) return all;
    const me = profile?.usuario_app ?? '';
    return all.filter((r) => (r.fields.Vendedor_RO ?? '') === me);
  }, [operacionesQ.data, isVentasRole, profile?.usuario_app]);

  const counts = useMemo(() => {
    let cotizacionesActuales = 0;
    let ventasConfirmacion = 0;
    let ventasConcretadas = 0;
    for (const row of scoped) {
      const f = row.fields;
      if (f.MesAnio_RO !== monthKey) continue;
      const tipo = f.Tipo_RO;
      const status = f.Status_RO ?? '';
      if (tipo === 'Cotizacion' && status !== 'Anulado') cotizacionesActuales++;
      if (tipo === 'Venta' && status === 'Confirmacion de Pago') ventasConfirmacion++;
      if (tipo === 'Venta' && VENTAS_CONCRETADAS_STATUSES.has(status)) ventasConcretadas++;
    }
    return { cotizacionesActuales, ventasConfirmacion, ventasConcretadas };
  }, [scoped, monthKey]);

  // --- SIRCREB auto-open ----------------------------------------------
  const [sircrebOpen, setSircrebOpen] = useState(false);
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (!canUpdateSircreb || autoOpenedRef.current) return;
    if (sircrebQ.isPending || !sircrebQ.data) return;
    if (!isParametroUpToDate(sircrebQ.data)) {
      autoOpenedRef.current = true;
      setSircrebOpen(true);
    }
  }, [canUpdateSircreb, sircrebQ.isPending, sircrebQ.data]);

  const sircrebValue = sircrebPercent(sircrebQ.data ?? null);
  const sircrebFresh = isParametroUpToDate(sircrebQ.data ?? null);

  // --- Operaciones activas filters ------------------------------------
  const [tipoFilter, setTipoFilter] = useState<TipoFilter>('Todos');
  const [statusFilter, setStatusFilter] = useState<StatusOption[]>(DEFAULT_STATUSES);
  const filterActive =
    tipoFilter !== 'Todos' ||
    statusFilter.length !== DEFAULT_STATUSES.length ||
    statusFilter.some((s) => !DEFAULT_STATUSES.includes(s));

  const operacionesActivas = useMemo(() => {
    return scoped
      .filter((r) => {
        const status = (r.fields.Status_RO ?? '') as StatusOption;
        if (!statusFilter.includes(status)) return false;
        if (tipoFilter !== 'Todos' && r.fields.Tipo_RO !== tipoFilter) return false;
        return true;
      })
      .sort((a, b) => {
        // Most recent first — `lastModifiedDateTime` is reliable, stored as ISO.
        const aT = a.lastModifiedDateTime ?? '';
        const bT = b.lastModifiedDateTime ?? '';
        return bT.localeCompare(aT);
      });
  }, [scoped, tipoFilter, statusFilter]);

  // --- Render ---------------------------------------------------------
  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Hola,{' '}
          <span className="text-emerald-600">
            {profile?.nombre || profile?.usuario_app || '—'}!
          </span>
        </h1>
        <div className="flex items-center gap-2">
          <FiltrarPopover
            tipoFilter={tipoFilter}
            setTipoFilter={setTipoFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            active={filterActive}
            onReset={() => {
              setTipoFilter('Todos');
              setStatusFilter(DEFAULT_STATUSES);
            }}
          />
          <AlertasHome />
        </div>
      </div>

      <section className="shrink-0 space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Resumen Mensual
        </h2>
        <div
          className={cn(
            'grid gap-4',
            // Admin sees 4 KPIs → render as 2×2 on mobile (and 4-up on
            // very wide screens). The 3-card variant stays single-column
            // on mobile because a 2+1 layout looks orphan.
            canUpdateSircreb ? 'grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3',
          )}
        >
          {canUpdateSircreb && (
            <SircrebCard
              value={sircrebValue}
              fresh={sircrebFresh}
              loading={sircrebQ.isPending}
              onClick={() => setSircrebOpen(true)}
            />
          )}
          <KpiCard
            label="Cotizaciones Actuales"
            value={counts.cotizacionesActuales}
            icon={ScrollText}
            tone="rose"
            loading={operacionesQ.isPending}
          />
          <KpiCard
            label="Ventas en Confirmación"
            value={counts.ventasConfirmacion}
            icon={ReceiptText}
            tone="emerald"
            loading={operacionesQ.isPending}
          />
          <KpiCard
            label="Ventas Concretadas"
            value={counts.ventasConcretadas}
            icon={TrendingUp}
            tone="indigo"
            loading={operacionesQ.isPending}
          />
        </div>
      </section>

      {isVentasRole && (discountLoading || configuredBrackets.length > 0) && (
        <section className="shrink-0 space-y-3">
          <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            Descuentos Disponibles
          </h2>
          <div className="grid gap-4 md:grid-cols-3">
            {configuredBrackets.map((b) => (
              <DescuentoCard
                key={b}
                bracket={b}
                pct={bracketPercents[b]}
                remaining={discountRemaining[b]}
                loading={discountLoading}
              />
            ))}
          </div>
        </section>
      )}

      <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        <h2 className="shrink-0 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Operaciones Activas
        </h2>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto pr-1">
          {operacionesQ.isPending ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
            </div>
          ) : operacionesQ.isError ? (
            <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center text-sm text-muted-foreground">
              No se pudieron cargar las operaciones.
            </div>
          ) : operacionesActivas.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-card/40 p-10 text-center text-sm text-muted-foreground">
              No hay operaciones que coincidan con los filtros aplicados.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {operacionesActivas.map((row) => (
                <OperacionCard
                  key={row.id}
                  row={row}
                  onOpen={() => navigate(`/ventas/${row.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {canUpdateSircreb && (
        <SircrebUpdateDialog
          item={sircrebQ.data ?? null}
          open={sircrebOpen}
          onOpenChange={setSircrebOpen}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------

type Tone = 'amber' | 'rose' | 'emerald' | 'indigo';

const TONE_CLS: Record<Tone, string> = {
  amber: 'bg-amber-100 text-amber-700',
  rose: 'bg-rose-100 text-rose-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  indigo: 'bg-indigo-100 text-indigo-700',
};

function KpiCard({
  label,
  value,
  icon: Icon,
  tone,
  loading,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: Tone;
  loading?: boolean;
}) {
  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-3 overflow-hidden rounded-2xl border bg-card px-3 py-4 shadow-sm sm:px-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-12" />
        ) : (
          <p className="mt-1 text-3xl font-semibold text-foreground">{value}</p>
        )}
      </div>
      <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', TONE_CLS[tone])}>
        <Icon className="h-5 w-5" aria-hidden />
      </span>
    </div>
  );
}

function DescuentoCard({
  bracket,
  pct,
  remaining,
  loading,
}: {
  bracket: DiscountBracket;
  pct: number;
  remaining: number;
  loading: boolean;
}) {
  const tone: Tone = bracket === 'C' ? 'rose' : bracket === 'B' ? 'amber' : 'emerald';
  return (
    <div className="flex w-full min-w-0 items-center justify-between gap-3 overflow-hidden rounded-2xl border bg-card px-3 py-4 shadow-sm sm:px-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-muted-foreground">Descuento {bracket} · {pct}%</p>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-32" />
        ) : (
          <p className="mt-1 flex flex-wrap items-baseline gap-1 text-3xl font-semibold text-foreground">
            {remaining}
            <span className="text-sm font-normal text-muted-foreground">disponibles</span>
          </p>
        )}
      </div>
      <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', TONE_CLS[tone])}>
        <Wallet className="h-5 w-5" aria-hidden />
      </span>
    </div>
  );
}

function SircrebCard({
  value,
  fresh,
  loading,
  onClick,
}: {
  value: number | null;
  fresh: boolean;
  loading: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-w-0 items-center justify-between gap-3 overflow-hidden rounded-2xl border bg-card px-3 py-4 text-left shadow-sm transition-colors hover:border-foreground/40 sm:px-4"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm text-muted-foreground">Valor SIRCREB</p>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              fresh
                ? 'bg-foreground text-background'
                : 'bg-rose-100 text-rose-700',
            )}
          >
            {fresh ? 'Actualizado' : 'Pendiente'}
          </span>
        </div>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-20" />
        ) : (
          <p className="mt-1 truncate text-3xl font-semibold text-foreground">
            {value != null
              ? `${value.toLocaleString('es-AR', { maximumFractionDigits: 4 })}%`
              : '—'}
          </p>
        )}
      </div>
      <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', TONE_CLS.amber)}>
        <Wallet className="h-5 w-5" aria-hidden />
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Operación card + filter popover
// ---------------------------------------------------------------------------

function OperacionCard({
  row,
  onOpen,
}: {
  row: ResumenOperacionesItem;
  onOpen: () => void;
}) {
  // Home currency follows the active country (ARS on AR, MXN on MX) so
  // a venta with no Moneda_RO defaults to the local currency, not always
  // ARS. The previous fallback rendered MX ventas as ARS amounts.
  const { config: activeCountry } = useActiveCountry();
  const f = row.fields;
  const tipo = f.Tipo_RO ?? '';
  const status = f.Status_RO ?? '';
  const moneda =
    f.Moneda_RO === 'USD'
      ? 'USD'
      : f.Moneda_RO === 'MXN'
        ? 'MXN'
        : f.Moneda_RO === 'ARS'
          ? 'ARS'
          : activeCountry.currency.primary;
  // Show the actually-cobrable amount (drops IVA on the non-facturado
  // portion when %factura<100; adds payment-method recargo when present).
  // Falls back to `TotalCotizacion_RO` for legacy rows that never
  // persisted the cobro column AND for freshly-created cotizaciones
  // where MontoTotalCobro is initialised to `0`. `?? f.TotalCotizacion`
  // alone doesn't cover the latter — cobrable=0 isn't null, so we'd
  // surface $0 on the recent-ventas card. Treat cobrable<=0 as fallback.
  const cobrableHome = parseMoney(f.MontoTotalCobro_RO);
  const monto =
    cobrableHome != null && cobrableHome > 0
      ? cobrableHome
      : parseMoney(f.TotalCotizacion_RO);
  const statusPill = statusPillFor(status);
  const tipoPill = tipoPillFor(tipo);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full min-w-0 flex-col gap-3 overflow-hidden rounded-2xl border bg-card p-4 text-left shadow-sm transition-colors hover:border-foreground/40"
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-foreground">
            {f.Cliente_RO?.trim() || 'Sin cliente'}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            ID Operación: {row.id}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
            statusPill.cls,
          )}
        >
          {statusPill.label}
        </span>
      </div>
      <div className="flex min-w-0 items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">Cotización:</p>
          <p className="truncate text-lg font-semibold text-foreground">
            {monto != null ? formatMoney(monto, moneda) : '—'}
          </p>
        </div>
        {tipoPill && (
          <span
            className={cn(
              'shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide',
              tipoPill.cls,
            )}
          >
            {tipoPill.label}
          </span>
        )}
      </div>
    </button>
  );
}

function statusPillFor(status: string): { label: string; cls: string } {
  switch (status) {
    case 'Cotizado':
      return { label: 'Cotizado', cls: 'bg-emerald-100 text-emerald-700' };
    case 'Pendiente de Pago':
      return { label: 'Pend. Pago', cls: 'bg-amber-100 text-amber-700' };
    case 'Confirmacion de Pago':
      return { label: 'Conf. Pago', cls: 'bg-sky-100 text-sky-700' };
    case 'Pago Parcial':
      return { label: 'Pago Parcial', cls: 'bg-indigo-100 text-indigo-700' };
    case 'Pago Realizado':
      return { label: 'Pago Realizado', cls: 'bg-emerald-100 text-emerald-700' };
    case 'Pendiente Despacho':
      return { label: 'Pend. Despacho', cls: 'bg-violet-100 text-violet-700' };
    case 'Pendiente Puesta Marcha':
      return { label: 'Pend. P. Marcha', cls: 'bg-fuchsia-100 text-fuchsia-700' };
    case 'Cerrado':
      return { label: 'Cerrado', cls: 'bg-slate-200 text-slate-700' };
    case 'Anulado':
      return { label: 'Anulado', cls: 'bg-rose-100 text-rose-700' };
    default:
      return { label: status || '—', cls: 'bg-slate-100 text-slate-700' };
  }
}

function tipoPillFor(tipo: string): { label: string; cls: string } | null {
  if (tipo === 'Cotizacion') {
    return { label: 'Cotización', cls: 'bg-amber-100 text-amber-700' };
  }
  if (tipo === 'Venta') {
    return { label: 'Venta', cls: 'bg-sky-100 text-sky-700' };
  }
  return null;
}

function FiltrarPopover({
  tipoFilter,
  setTipoFilter,
  statusFilter,
  setStatusFilter,
  active,
  onReset,
}: {
  tipoFilter: TipoFilter;
  setTipoFilter: (next: TipoFilter) => void;
  statusFilter: StatusOption[];
  setStatusFilter: (next: StatusOption[]) => void;
  active: boolean;
  onReset: () => void;
}) {
  const [open, setOpen] = useState(false);
  const toggleStatus = (s: StatusOption) => {
    setStatusFilter(
      statusFilter.includes(s)
        ? statusFilter.filter((x) => x !== s)
        : [...statusFilter, s],
    );
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-12 gap-2', active && 'border-foreground/60')}
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Tipo
          </p>
          <Select
            value={tipoFilter}
            onValueChange={(v) => setTipoFilter(v as TipoFilter)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Todos">Todos</SelectItem>
              <SelectItem value="Venta">Venta</SelectItem>
              <SelectItem value="Cotizacion">Cotización</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Estado
          </p>
          <div className="flex flex-col gap-1">
            {STATUS_OPTIONS.map((s) => {
              const checked = statusFilter.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={cn(
                    'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
                    checked
                      ? 'border-foreground bg-foreground/5 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-secondary',
                  )}
                >
                  <span>{s}</span>
                  {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Alerta de CAE huérfanos — facturas emitidas en ARCA cuyo cobro o cuya venta
 * se anuló y que nadie revirtió con una Nota de Crédito.
 *
 * Anular es un soft-delete de una sola columna (`Status_P='Anulado'`): `cae`,
 * `nro_comprobante` y `cae_vencimiento` quedan intactos, así que el
 * comprobante sigue existiendo ante ARCA. La única salida es emitir la NC.
 *
 * Se muestra sólo a los roles que pueden emitirla (`/solicitar-nota-credito`
 * es admin/administración) y NO se puede descartar: mientras haya un huérfano
 * el aviso queda. Reusa `listFacturasArca()` en vez de un endpoint nuevo — ya
 * devuelve todas las facturas AR (el volumen es chico) y trae `saldo` y
 * `ncTotal` calculados.
 */
function CaeHuerfanosAlert({ onCount }: { onCount: ReportarAlerta }) {
  const navigate = useNavigate();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const puedeEmitirNc =
    profile?.perfil === 'admin' || profile?.perfil === 'administracion';
  const enabled = puedeEmitirNc && countryCode === 'AR';

  const facturasQ = useQuery({
    queryKey: ['facturacion', countryCode],
    queryFn: listFacturasArca,
    enabled,
    staleTime: 60_000,
  });

  const hoy = hoyAr();
  const huerfanas = useMemo(
    () =>
      (facturasQ.data ?? [])
        .filter(esCaeHuerfano)
        // La más urgente (o la más vencida) primero, igual que el aviso rojo.
        .sort(
          (a, b) =>
            (plazoNc(a.fechaEmision, hoy)?.dias ?? 0) - (plazoNc(b.fechaEmision, hoy)?.dias ?? 0),
        ),
    [facturasQ.data, hoy],
  );
  useEffect(
    () => onCount('cae', enabled ? huerfanas.length : 0, 'ambar', 'CAE huérfanos'),
    [onCount, enabled, huerfanas.length],
  );
  const [verDetalle, setVerDetalle] = useState(false);
  // NC emitida desde acá mismo, sin ir a la venta → Pagos → Ver facturas.
  const [ncFor, setNcFor] = useState<{ id: string; nro: string } | null>(null);
  if (!enabled || (huerfanas.length === 0 && !ncFor)) return null;

  const totalSaldo = huerfanas.reduce((a, r) => a + r.saldo, 0);
  const resumenPlazo = resumirPlazos(huerfanas.map((r) => plazoNc(r.fechaEmision, hoy)));
  // ¿Pasaron por Aprobaciones? Las "sin aprobación" son anulaciones directas
  // previas al bloqueo — las que conviene revisar.
  const aprobadas = huerfanas.filter((r) => r.anulacionAprobada).length;
  const sinAprobacion = huerfanas.length - aprobadas;
  return (
    <>
      {/* Altura fija: sólo el resumen. El detalle va al diálogo — con 20
          huérfanas una lista inline empujaba el dashboard entero para abajo. */}
      <section className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <AlertTriangle
            className="h-5 w-5 shrink-0 text-amber-600"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-amber-900">
              {huerfanas.length === 1
                ? '1 factura pendiente de Nota de Crédito'
                : `${huerfanas.length} facturas pendientes de Nota de Crédito`}{' '}
              · {formatMoney(totalSaldo, 'ARS')}
              {resumenPlazo && <span className="font-medium"> · {resumenPlazo}</span>}
              {aprobadas > 0 && (
                <span className="font-medium text-emerald-800">
                  {' '}· {aprobadas} aprobada{aprobadas === 1 ? '' : 's'}
                </span>
              )}
              {sinAprobacion > 0 && (
                <span className="font-medium text-rose-700"> · {sinAprobacion} sin aprobación</span>
              )}
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-amber-900/80">
              El cobro o la venta ya se anuló, pero la factura sigue vigente en ARCA.
              Hay que emitir la <strong>Nota de Crédito</strong> antes del fin del mes
              en que se emitió.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVerDetalle(true)}
            className="shrink-0 border-amber-400 bg-white text-amber-900 hover:bg-amber-100"
          >
            Ver detalle
          </Button>
        </div>
      </section>

      <Dialog open={verDetalle} onOpenChange={setVerDetalle}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Facturas pendientes de Nota de Crédito</DialogTitle>
            <DialogDescription>
              {huerfanas.length} comprobante{huerfanas.length === 1 ? '' : 's'} por{' '}
              {formatMoney(totalSaldo, 'ARS')}. Cada uno necesita una Nota de
              Crédito para darse de baja ante ARCA.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[60vh] overflow-auto p-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-4 py-2 font-medium">Comprobante</th>
                  <th className="px-4 py-2 font-medium">Cliente</th>
                  <th className="whitespace-nowrap px-4 py-2 font-medium">Motivo</th>
                  <th className="px-4 py-2 font-medium">Plazo NC</th>
                  <th className="px-4 py-2 text-right font-medium">Saldo</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {huerfanas.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-foreground">
                      {r.nroComprobante ?? '—'}
                      <div className="text-xs font-normal text-muted-foreground">
                        Emitida {fechaAr(r.fechaEmision)}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <div className="min-w-[10rem]">{r.cliente ?? '—'}</div>
                      {r.nroCotizacion && (
                        <div className="whitespace-nowrap text-xs">Venta {r.nroCotizacion}</div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-muted-foreground">
                      {r.ventaAnulada ? 'Venta anulada' : 'Cobro anulado'}
                      <AprobacionAnulacion a={r.anulacionAprobada} />
                    </td>
                    <td className="px-4 py-2">
                      <PlazoBadge fechaEmision={r.fechaEmision} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-foreground">
                      {formatMoney(r.saldo, 'ARS')}
                    </td>
                    <td className="px-4 py-2">
                      <AccionesFactura
                        onEmitirNc={() => {
                          setVerDetalle(false);
                          setNcFor({ id: r.id, nro: r.nroComprobante ?? '' });
                        }}
                        onVerVenta={
                          r.ventaSpId
                            ? () => {
                                setVerDetalle(false);
                                navigate(`/ventas/${r.ventaSpId}`);
                              }
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerDetalle(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ncFor && (
        <GenerarNotaCreditoDialog
          facturaReceiptId={ncFor.id}
          nroFactura={ncFor.nro}
          // Volver al detalle: si hay más facturas pendientes se sigue con la
          // próxima; si no queda ninguna, el aviso entero desaparece.
          onClose={() => {
            setNcFor(null);
            setVerDetalle(true);
          }}
        />
      )}
    </>
  );
}


/**
 * ¿La anulación pasó por Aprobaciones? "Aprobada por Diego · 17/09/2026", o
 * "Sin aprobación" si se anuló directo (casos previos al bloqueo).
 */
function AprobacionAnulacion({ a }: { a: FacturaArcaRow['anulacionAprobada'] }) {
  if (!a) {
    return <div className="text-xs font-medium text-amber-700">Sin aprobación</div>;
  }
  return (
    <div className="text-xs text-emerald-700">
      {['Aprobada', a.por && `por ${a.por}`].filter(Boolean).join(' ')}
      {a.fecha ? ` · ${fechaAr(a.fecha)}` : ''}
    </div>
  );
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY'. */
function fechaAr(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/** Resumen del banner: "2 vencidas · la próxima vence en 13 días". */
function resumirPlazos(plazos: Array<PlazoNc | null>): string {
  const validos = plazos.filter((p): p is PlazoNc => p != null);
  const vencidas = validos.filter((p) => p.dias < 0).length;
  const proxima = validos
    .filter((p) => p.dias >= 0)
    .sort((a, b) => a.dias - b.dias)[0];
  return [
    vencidas > 0 ? `${vencidas} vencida${vencidas === 1 ? '' : 's'}` : null,
    proxima ? `${validos.length === 1 ? '' : 'la próxima '}${textoPlazo(proxima)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Acciones de una fila de los avisos fiscales. "Emitir NC" abre el mismo
 * diálogo que Ver facturas (dry-run + emisión), sin tener que ir a la venta.
 * Ausente para quien no puede emitirla.
 */
function AccionesFactura({
  onEmitirNc,
  onSolicitarAnulacion,
  anulacionSolicitada = false,
  onVerVenta,
}: {
  onEmitirNc?: () => void;
  /** Vendedores (no emiten NC): pedir la anulación del cobro a un aprobador. */
  onSolicitarAnulacion?: () => void;
  /** Ya hay una solicitud pendiente: se muestra en lugar del botón. */
  anulacionSolicitada?: boolean;
  onVerVenta?: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      {anulacionSolicitada ? (
        <span className="whitespace-nowrap rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
          Anulación solicitada
        </span>
      ) : (
        onSolicitarAnulacion && (
          <Button
            size="sm"
            variant="outline"
            className="whitespace-nowrap border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={onSolicitarAnulacion}
          >
            Solicitar anulación
          </Button>
        )
      )}
      {onEmitirNc && (
        <Button size="sm" className="whitespace-nowrap" onClick={onEmitirNc}>
          Emitir NC
        </Button>
      )}
      {onVerVenta && (
        <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={onVerVenta}>
          Ver venta
        </Button>
      )}
    </div>
  );
}

/**
 * "vence en N días" / "vencida hace N días" hasta el fin del mes de emisión de
 * la factura (`plazoNc`). Rojo si ya venció o vence hoy, ámbar la última
 * semana, neutro antes.
 */
function PlazoBadge({ fechaEmision }: { fechaEmision: string }) {
  const p = plazoNc(fechaEmision, hoyAr());
  if (!p) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        'inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium',
        p.dias <= 0
          ? 'bg-rose-100 text-rose-700'
          : p.dias <= 7
            ? 'bg-amber-100 text-amber-800'
            : 'bg-secondary text-foreground',
      )}
      title={`Fin del mes de emisión: ${p.vence.slice(8, 10)}/${p.vence.slice(5, 7)}`}
    >
      {textoPlazo(p)}
    </span>
  );
}

/**
 * Facturas ANTICIPADAS (Facturación Previa) emitidas y todavía sin cobrar.
 * Pedido del cliente (2026-09-17): si antes de fin del mes en que se emitió
 * la factura no se cobra ni se le emite la Nota de Crédito, queda fuera de
 * término. Rojo y con los días que quedan; las vencidas se siguen mostrando.
 *
 * Lo ven admin/administración (todas) y ventas/marketing (las de sus ventas,
 * que también pueden anular): el filtro por rol lo hace el server. No se
 * descarta — desaparece cuando la factura se cobra o se le emite la NC.
 */
function FacturasAnticipadasAlert({ onCount }: { onCount: ReportarAlerta }) {
  const navigate = useNavigate();
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const perfil = profile?.perfil;
  // Vendedores ven el aviso (sus ventas), pero la NC la emite Administración.
  const puedeEmitirNc = perfil === 'admin' || perfil === 'administracion';
  const enabled =
    countryCode === 'AR' &&
    (perfil === 'admin' ||
      perfil === 'administracion' ||
      perfil === 'ventas' ||
      perfil === 'marketing');

  const facturasQ = useQuery({
    // Bajo el prefijo ['facturacion']: anular, cobrar o emitir una NC ya lo
    // invalidan.
    queryKey: ['facturacion', countryCode, 'anticipadas-pendientes'],
    queryFn: listFacturasAnticipadasPendientes,
    enabled,
    staleTime: 60_000,
  });

  const hoy = hoyAr();
  const filas = useMemo(
    () =>
      (facturasQ.data ?? [])
        .map((r: FacturaArcaRow) => ({ r, plazo: plazoNc(r.fechaEmision, hoy) }))
        // La más urgente (o la más vencida) primero.
        .sort((a, b) => (a.plazo?.dias ?? 0) - (b.plazo?.dias ?? 0)),
    [facturasQ.data, hoy],
  );
  useEffect(
    () =>
      onCount(
        'anticipadas',
        enabled ? filas.length : 0,
        'rojo',
        'Facturas anticipadas sin cobrar',
      ),
    [onCount, enabled, filas.length],
  );
  const [verDetalle, setVerDetalle] = useState(false);
  const [ncFor, setNcFor] = useState<{ id: string; nro: string } | null>(null);
  // Vendedores: pedir la anulación del cobro desde el aviso. Aprobada, la
  // factura pasa al aviso amarillo y Administración emite la NC.
  const [solicitarFor, setSolicitarFor] = useState<FacturaArcaRow | null>(null);
  const qc = useQueryClient();
  const solicitar = useMutation({
    mutationFn: (r: FacturaArcaRow) => solicitarAnulacionComprobante(r.ventaSpId!, r.id),
    onSuccess: (res) => {
      if (res.outcome === 'pending_approval_created') {
        toast.success('Solicitud de anulación enviada', {
          description: 'Un administrador tiene que aprobarla desde Aprobaciones.',
        });
      } else {
        toast.info('Ya hay una solicitud pendiente', {
          description: 'La anulación de este cobro ya está esperando aprobación.',
        });
      }
      qc.invalidateQueries({ queryKey: ['facturacion'] });
      qc.invalidateQueries({ queryKey: ['aprobaciones'] });
      setSolicitarFor(null);
      setVerDetalle(true);
    },
    onError: (err) => {
      toast.error('No se pudo enviar la solicitud', {
        description: err instanceof ApiError && err.message ? err.message : 'Error desconocido',
      });
      setSolicitarFor(null);
      setVerDetalle(true);
    },
  });
  if (!enabled || (filas.length === 0 && !ncFor)) return null;

  const total = filas.reduce((a, f) => a + f.r.saldo, 0);
  const resumenPlazo = resumirPlazos(filas.map((f) => f.plazo));

  return (
    <>
      <section className="rounded-2xl border border-rose-300 bg-rose-50 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-rose-600" aria-hidden />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-rose-900">
              {filas.length === 1
                ? '1 factura anticipada sin cobrar'
                : `${filas.length} facturas anticipadas sin cobrar`}{' '}
              · {formatMoney(total, 'ARS')}
              {resumenPlazo && <span className="font-medium"> · {resumenPlazo}</span>}
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-rose-900/80">
              Si no se cobran o no se emite la <strong>Nota de Crédito</strong> antes
              del fin del mes en que se emitió cada factura, quedan fuera de término.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVerDetalle(true)}
            className="shrink-0 border-rose-400 bg-white text-rose-900 hover:bg-rose-100"
          >
            Ver detalle
          </Button>
        </div>
      </section>

      <Dialog open={verDetalle} onOpenChange={setVerDetalle}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Facturas anticipadas sin cobrar</DialogTitle>
            <DialogDescription>
              {filas.length} factura{filas.length === 1 ? '' : 's'} por{' '}
              {formatMoney(total, 'ARS')}. Cada una se cobra o se revierte con una
              Nota de Crédito antes del fin del mes en que se emitió.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[60vh] overflow-auto p-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-4 py-2 font-medium">Comprobante</th>
                  <th className="px-4 py-2 font-medium">Cliente</th>
                  <th className="px-4 py-2 font-medium">Plazo</th>
                  <th className="px-4 py-2 text-right font-medium">Saldo</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filas.map(({ r }) => (
                  <tr key={r.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-4 py-2 font-medium text-foreground">
                      {r.nroComprobante ?? '—'}
                      <div className="text-xs font-normal text-muted-foreground">
                        Emitida {fechaAr(r.fechaEmision)}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <div className="min-w-[10rem]">{r.cliente ?? '—'}</div>
                      {r.nroCotizacion && (
                        <div className="whitespace-nowrap text-xs">Venta {r.nroCotizacion}</div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <PlazoBadge fechaEmision={r.fechaEmision} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-foreground">
                      {formatMoney(r.saldo, 'ARS')}
                    </td>
                    <td className="px-4 py-2">
                      <AccionesFactura
                        anulacionSolicitada={r.anulacionSolicitada}
                        onSolicitarAnulacion={
                          !puedeEmitirNc && r.ventaSpId
                            ? () => {
                                setVerDetalle(false);
                                setSolicitarFor(r);
                              }
                            : undefined
                        }
                        onEmitirNc={
                          puedeEmitirNc
                            ? () => {
                                setVerDetalle(false);
                                setNcFor({ id: r.id, nro: r.nroComprobante ?? '' });
                              }
                            : undefined
                        }
                        onVerVenta={
                          r.ventaSpId
                            ? () => {
                                setVerDetalle(false);
                                navigate(`/ventas/${r.ventaSpId}`);
                              }
                            : undefined
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerDetalle(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {ncFor && (
        <GenerarNotaCreditoDialog
          facturaReceiptId={ncFor.id}
          nroFactura={ncFor.nro}
          // Volver al detalle: si hay más facturas pendientes se sigue con la
          // próxima; si no queda ninguna, el aviso entero desaparece.
          onClose={() => {
            setNcFor(null);
            setVerDetalle(true);
          }}
        />
      )}

      <Dialog
        open={solicitarFor !== null}
        onOpenChange={(o) => {
          if (!o && !solicitar.isPending) {
            setSolicitarFor(null);
            setVerDetalle(true);
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          processing={solicitar.isPending && 'Enviando solicitud…'}
        >
          <DialogHeader>
            <DialogTitle>Solicitar anulación</DialogTitle>
            <DialogDescription>
              Se pide anular el cobro de la factura{' '}
              <strong>{solicitarFor?.nroComprobante ?? '—'}</strong>
              {solicitarFor?.cliente ? ` de ${solicitarFor.cliente}` : ''} por{' '}
              {formatMoney(solicitarFor?.saldo ?? 0, 'ARS')}.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
              La tiene que aprobar un administrador. Una vez aprobada, la factura
              queda para que Administración emita la <strong>Nota de Crédito</strong>{' '}
              antes del fin del mes en que se emitió.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={solicitar.isPending}
              onClick={() => {
                setSolicitarFor(null);
                setVerDetalle(true);
              }}
            >
              Volver
            </Button>
            <Button
              disabled={solicitar.isPending}
              className="bg-rose-600 text-white hover:bg-rose-700"
              onClick={() => solicitarFor && solicitar.mutate(solicitarFor)}
            >
              Solicitar anulación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type PromesaConDias = { p: PromesaCobro; dias: number };

/** "$ 1.000 · US$ 50": pesos y dólares por separado (el monto está en la moneda de la caja). */
function totalesPorMoneda(filas: PromesaConDias[]): string {
  const ars = filas.filter((f) => f.p.moneda === 'ARS').reduce((a, f) => a + f.p.monto, 0);
  const usd = filas.filter((f) => f.p.moneda === 'USD').reduce((a, f) => a + f.p.monto, 0);
  return [ars ? formatMoney(ars, 'ARS') : null, usd ? formatMoney(usd, 'USD') : null]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Promesas de cobro vencidas (rojo) y por vencer en los próximos
 * POR_VENCER_DIAS días corridos (ámbar). Pedido del cliente 2026-10-20: que
 * las vea todo el mundo, sobre todo la fuerza de venta.
 *
 * Admin/administración ven todas; ventas/marketing las de sus ventas (filtra
 * el server). Cada aviso desaparece cuando no tiene nada.
 */
function PromesasCobroAlerts({
  onCount,
  mostrar,
}: {
  onCount: ReportarAlerta;
  /** Cuál de los dos avisos dibuja (la query es la misma, React Query la comparte). */
  mostrar: 'vencidas' | 'porVencer';
}) {
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const countryCode = activeCountry.code;
  const perfil = profile?.perfil;
  const veTodas = perfil === 'admin' || perfil === 'administracion';
  const enabled = countryCode === 'AR' && (veTodas || perfil === 'ventas' || perfil === 'marketing');

  const promesasQ = useQuery({
    // Bajo ['administracion']: confirmar / registrar cobros ya lo invalidan.
    queryKey: ['administracion', countryCode, 'promesas-cobro'],
    queryFn: listPromesasCobro,
    enabled,
    staleTime: 60_000,
  });

  const hoy = hoyAr();
  const { vencidas, porVencer } = useMemo(() => {
    const conDias = (promesasQ.data ?? []).flatMap((p) => {
      const dias = diasHasta(p.fechaCobro, hoy);
      return dias == null ? [] : [{ p, dias }];
    });
    return {
      vencidas: conDias.filter((f) => estadoPromesa(f.dias) === 'vencida'),
      porVencer: conDias.filter((f) => estadoPromesa(f.dias) === 'por_vencer'),
    };
  }, [promesasQ.data, hoy]);
  useEffect(
    () =>
      onCount(
        'vencidas',
        enabled ? vencidas.length : 0,
        'rojo',
        'Promesas de cobro vencidas',
      ),
    [onCount, enabled, vencidas.length],
  );
  useEffect(
    () =>
      onCount(
        'porVencer',
        enabled ? porVencer.length : 0,
        'ambar',
        `Promesas que vencen en ${POR_VENCER_DIAS} días`,
      ),
    [onCount, enabled, porVencer.length],
  );

  if (!enabled) return null;
  return mostrar === 'vencidas' ? (
    <PromesasBanner
      tono="rojo"
      filas={vencidas}
      titulo={(n) => (n === 1 ? '1 promesa de cobro vencida' : `${n} promesas de cobro vencidas`)}
      texto="La fecha prometida ya pasó y el cobro no se registró. Hay que cobrarlas o reprogramarlas."
      mostrarVendedor={veTodas}
    />
  ) : (
    <PromesasBanner
      tono="ambar"
      filas={porVencer}
      titulo={(n) =>
        `${n === 1 ? '1 promesa de cobro vence' : `${n} promesas de cobro vencen`} en los próximos ${POR_VENCER_DIAS} días`
      }
      texto="Conviene contactar al cliente antes de la fecha prometida."
      mostrarVendedor={veTodas}
    />
  );
}

/**
 * E-cheqs autorizados (AR): rojo los que ya llegaron a su fecha de pago y se
 * pueden acreditar, ámbar los que se cobran en los próximos POR_VENCER_DIAS
 * días. Sólo admin/administración, que son quienes los acreditan.
 */
function EcheqsAlerts({ onCount, mostrar }: { onCount: ReportarAlerta; mostrar: 'cobrables' | 'proximos' }) {
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const navigate = useNavigate();
  const enabled =
    activeCountry.code === 'AR' && (profile?.perfil === 'admin' || profile?.perfil === 'administracion');
  const q = useQuery({
    // Bajo ['administracion']: acreditar / rechazar ya lo invalidan.
    queryKey: ['administracion', activeCountry.code, 'echeqs', 'autorizados'],
    queryFn: () => listEcheqs({ estado: ['Autorizado'] }),
    enabled,
    staleTime: 60_000,
  });
  const hoy = hoyAr();
  const { cobrables, proximos } = useMemo(() => {
    const conDias = (q.data ?? []).flatMap((e) => {
      const dias = diasHasta(e.fechaPago, hoy);
      return dias == null ? [] : [{ e, dias }];
    });
    return {
      cobrables: conDias.filter((f) => f.dias <= 0),
      proximos: conDias.filter((f) => f.dias > 0 && f.dias <= POR_VENCER_DIAS),
    };
  }, [q.data, hoy]);
  useEffect(
    () => onCount('echeqsCobrables', enabled ? cobrables.length : 0, 'rojo', 'E-cheqs para acreditar'),
    [onCount, enabled, cobrables.length],
  );
  useEffect(
    () =>
      onCount(
        'echeqsProximos',
        enabled ? proximos.length : 0,
        'ambar',
        `E-cheqs que se cobran en ${POR_VENCER_DIAS} días`,
      ),
    [onCount, enabled, proximos.length],
  );
  const filas = mostrar === 'cobrables' ? cobrables : proximos;
  if (!enabled || filas.length === 0) return null;
  const rojo = mostrar === 'cobrables';
  const total = filas.reduce((a, f) => a + f.e.monto, 0);
  return (
    <div
      className={cn(
        'rounded-xl border px-4 py-3 text-sm',
        rojo ? 'border-rose-200 bg-rose-50 text-rose-900' : 'border-amber-200 bg-amber-50 text-amber-900',
      )}
    >
      <p className="font-medium">
        {rojo
          ? `${filas.length === 1 ? '1 E-cheq ya se puede' : `${filas.length} E-cheqs ya se pueden`} acreditar`
          : `${filas.length === 1 ? '1 E-cheq se cobra' : `${filas.length} E-cheqs se cobran`} en los próximos ${POR_VENCER_DIAS} días`}
      </p>
      <ul className="mt-2 divide-y divide-current/10 text-xs">
        {filas.map(({ e, dias }) => (
          <li key={e.id} className="flex items-baseline justify-between gap-3 py-1.5">
            <span className="min-w-0 truncate">
              <span className="font-medium">{e.cliente ?? '—'}</span> · Venta {e.ventaId} · N° {e.nroCheque}
            </span>
            <span className="shrink-0 tabular-nums">
              {formatMoney(e.monto, 'ARS')} · {textoCobro(dias)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex items-center justify-between border-t border-current/10 pt-2 text-xs">
        <span className="font-semibold tabular-nums">Total {formatMoney(total, 'ARS')}</span>
        <Button size="sm" variant="outline" onClick={() => navigate('/administracion?tab=echeqs')}>
          Ir a E-cheqs
        </Button>
      </div>
    </div>
  );
}

/**
 * Lo que un admin aprobó o rechazó de lo que pidió este usuario (descuento,
 * E-cheq, Factura Anticipada, % de factura, cambio de horno, eliminación),
 * de los últimos 14 días. Cada una se descarta (queda guardado en la base).
 * Egress: sin polling, caché de 5 min; descartar actualiza la lista local
 * sin volver a pedirla.
 */
function ResolucionesAlert({ onCount }: { onCount: ReportarAlerta }) {
  const { config: activeCountry } = useActiveCountry();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ['alertas', activeCountry.code, 'resoluciones'];
  const q = useQuery({ queryKey, queryFn: listResoluciones, staleTime: 5 * 60_000 });
  const items = q.data ?? [];
  const descartar = useMutation({
    mutationFn: descartarAlertas,
    onMutate: (keys) => {
      const fuera = new Set(keys);
      const antes = qc.getQueryData<Resolucion[]>(queryKey);
      qc.setQueryData<Resolucion[]>(queryKey, (prev) => prev?.filter((r) => !fuera.has(r.key)));
      return { antes };
    },
    onError: (_err, _keys, ctx) => {
      qc.setQueryData(queryKey, ctx?.antes);
      toast.error('No se pudo descartar. Intentá nuevamente.');
    },
  });
  const rechazos = items.filter((r) => r.resultado === 'Rechazado').length;
  useEffect(
    () =>
      onCount(
        'resoluciones',
        items.length,
        rechazos > 0 ? 'rojo' : 'ambar',
        rechazos > 0 ? 'Aprobaciones y rechazos' : 'Aprobaciones',
      ),
    [onCount, items.length, rechazos],
  );
  if (items.length === 0) return null;
  return (
    <div className="rounded-xl border bg-card px-4 py-3 text-sm">
      <ul className="divide-y text-xs">
        {items.map((r) => (
          <li key={r.key} className="flex items-start gap-3 py-2">
            <span
              className={cn(
                'mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                r.resultado === 'Rechazado' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700',
              )}
            >
              {r.resultado === 'Rechazado' ? 'RECHAZADO' : 'APROBADO'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">{r.detalle}</p>
              {r.ventaId != null && (
                <button
                  type="button"
                  className="text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => navigate(`/ventas/${r.ventaId}`)}
                >
                  {r.cliente ?? '—'} · Venta {r.ventaId}
                </button>
              )}
              {r.motivo && <p className="text-rose-700">Motivo: {r.motivo}</p>}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => descartar.mutate([r.key])}
              aria-label="Descartar"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      {items.length > 1 && (
        <div className="mt-1 flex justify-end border-t pt-2">
          <Button size="sm" variant="outline" onClick={() => descartar.mutate(items.map((r) => r.key))}>
            Descartar todas
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * E-cheqs que el banco rechazó cuando el horno ya había salido (despacho
 * habilitado con cheques en cartera): la venta no vuelve atrás y ese monto
 * queda adeudado. Admin/administración; quedan hasta descartarlos.
 * Egress: sin polling, caché de 5 min; descartar actualiza la lista local.
 */
function EcheqsRebotadosAlert({ onCount }: { onCount: ReportarAlerta }) {
  const { profile } = useSession();
  const { config: activeCountry } = useActiveCountry();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const enabled =
    activeCountry.code === 'AR' && (profile?.perfil === 'admin' || profile?.perfil === 'administracion');
  const queryKey = ['alertas', activeCountry.code, 'echeqs-rebotados'];
  const q = useQuery({ queryKey, queryFn: listEcheqsRebotados, enabled, staleTime: 5 * 60_000 });
  const items = enabled ? q.data ?? [] : [];
  const descartar = useMutation({
    mutationFn: descartarAlertas,
    onMutate: (keys) => {
      const fuera = new Set(keys);
      const antes = qc.getQueryData<EcheqRebotado[]>(queryKey);
      qc.setQueryData<EcheqRebotado[]>(queryKey, (prev) => prev?.filter((r) => !fuera.has(r.key)));
      return { antes };
    },
    onError: (_err, _keys, ctx) => {
      qc.setQueryData(queryKey, ctx?.antes);
      toast.error('No se pudo descartar. Intentá nuevamente.');
    },
  });
  useEffect(
    () => onCount('echeqsRebotados', items.length, 'rojo', 'E-cheqs rechazados con el horno despachado'),
    [onCount, items.length],
  );
  if (items.length === 0) return null;
  const total = items.reduce((a, r) => a + r.monto, 0);
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
      <p className="font-medium">
        {items.length === 1 ? 'El banco rechazó 1 E-cheq' : `El banco rechazó ${items.length} E-cheqs`} de ventas ya
        despachadas: ese monto quedó sin cobrar.
      </p>
      <ul className="mt-2 divide-y divide-current/10 text-xs">
        {items.map((r) => (
          <li key={r.key} className="flex items-start gap-3 py-1.5">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                className="font-medium underline-offset-2 hover:underline"
                onClick={() => navigate(`/ventas/${r.ventaId}`)}
              >
                {r.cliente ?? '—'} · Venta {r.ventaId}
              </button>
              <p>
                N° {r.nroCheque} · {r.banco}
                {r.motivo ? ` · ${r.motivo}` : ''}
              </p>
            </div>
            <span className="shrink-0 tabular-nums font-semibold">{formatMoney(r.monto, 'ARS')}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => descartar.mutate([r.key])}
              aria-label="Descartar"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      {items.length > 1 && (
        <div className="mt-2 flex items-center justify-between border-t border-current/10 pt-2 text-xs">
          <span className="font-semibold tabular-nums">Total {formatMoney(total, 'ARS')}</span>
          <Button size="sm" variant="outline" onClick={() => descartar.mutate(items.map((r) => r.key))}>
            Descartar todas
          </Button>
        </div>
      )}
    </div>
  );
}

function PromesasBanner({
  tono,
  filas,
  titulo,
  texto,
  mostrarVendedor,
}: {
  tono: 'rojo' | 'ambar';
  filas: PromesaConDias[];
  titulo: (n: number) => string;
  texto: string;
  mostrarVendedor: boolean;
}) {
  const navigate = useNavigate();
  const [verDetalle, setVerDetalle] = useState(false);
  if (filas.length === 0) return null;
  const rojo = tono === 'rojo';
  const totales = totalesPorMoneda(filas);
  const cajas = resumenPorCaja(filas);
  const totalesMoneda = (['ARS', 'USD'] as const)
    .map((m) => ({ m, v: filas.filter((f) => f.p.moneda === m).reduce((a, f) => a + f.p.monto, 0) }))
    .filter((t) => t.v > 0);

  return (
    <>
      <section
        className={cn(
          'rounded-2xl border p-4',
          rojo ? 'border-rose-300 bg-rose-50' : 'border-amber-300 bg-amber-50',
        )}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle
            className={cn('mt-0.5 h-5 w-5 shrink-0', rojo ? 'text-rose-600' : 'text-amber-600')}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <h3 className={cn('text-sm font-semibold', rojo ? 'text-rose-900' : 'text-amber-900')}>
              {titulo(filas.length)}
            </h3>
            <p className={cn('mt-0.5 text-xs leading-relaxed', rojo ? 'text-rose-900/80' : 'text-amber-900/80')}>
              {texto}
            </p>
          </div>
        </div>
        {/* Subgrupos: por caja, cantidad y monto. */}
        <dl
          className={cn(
            'mt-3 divide-y rounded-xl bg-white/70 text-xs',
            rojo ? 'divide-rose-100 text-rose-900' : 'divide-amber-100 text-amber-900',
          )}
        >
          {cajas.map((c) => (
            <div key={`${c.medio}-${c.moneda}`} className="flex items-center justify-between gap-3 px-3 py-1.5">
              <dt className="min-w-0 truncate">
                {c.medio} <span className="opacity-70">· {c.n}</span>
              </dt>
              <dd className="whitespace-nowrap font-medium tabular-nums">{formatMoney(c.monto, c.moneda)}</dd>
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 px-3 py-1.5 font-semibold">
            <dt>Total</dt>
            <dd className="whitespace-nowrap tabular-nums">{totales}</dd>
          </div>
        </dl>
        <div className="mt-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setVerDetalle(true)}
            className={cn(
              'bg-white',
              rojo
                ? 'border-rose-400 text-rose-900 hover:bg-rose-100'
                : 'border-amber-400 text-amber-900 hover:bg-amber-100',
            )}
          >
            Ver detalle
          </Button>
        </div>
      </section>

      <Dialog open={verDetalle} onOpenChange={setVerDetalle}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>{titulo(filas.length)}</DialogTitle>
            <DialogDescription>{texto}</DialogDescription>
          </DialogHeader>
          <DialogBody className="max-h-[60vh] overflow-auto p-0">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="whitespace-nowrap px-4 py-2 font-medium">Fecha prometida</th>
                  <th className="px-4 py-2 font-medium">Cliente</th>
                  {mostrarVendedor && <th className="px-4 py-2 font-medium">Vendedor</th>}
                  <th className="px-4 py-2 font-medium">Caja</th>
                  <th className="px-4 py-2 text-right font-medium">Monto</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filas.map(({ p, dias }) => (
                  <tr key={p.id} className="hover:bg-muted/30">
                    <td className="whitespace-nowrap px-4 py-2">
                      <div className="font-medium text-foreground">{fechaAr(p.fechaCobro)}</div>
                      <span
                        className={cn(
                          'mt-0.5 inline-flex whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium',
                          dias < 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-800',
                        )}
                      >
                        {textoDias(dias)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      <div className="min-w-[10rem]">{p.cliente ?? '—'}</div>
                      {(p.nroCotizacion || p.ventaSpId) && (
                        <div className="whitespace-nowrap text-xs">Venta {p.nroCotizacion ?? p.ventaSpId}</div>
                      )}
                    </td>
                    {mostrarVendedor && <td className="px-4 py-2 text-muted-foreground">{p.vendedor ?? '—'}</td>}
                    <td className="px-4 py-2 text-muted-foreground">{p.medioPago ?? '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-foreground">
                      {formatMoney(p.monto, p.moneda)}
                      <div className="text-xs font-normal text-muted-foreground">
                        {p.esPagoFinal ? 'Pago Final' : 'Anticipo'}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {p.ventaSpId && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="whitespace-nowrap"
                          onClick={() => {
                            setVerDetalle(false);
                            navigate(`/ventas/${p.ventaSpId}/pagos`);
                          }}
                        >
                          Ver pagos
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 border-t-2 border-border bg-muted text-sm font-semibold">
                {totalesMoneda.map(({ m, v }) => (
                  <tr key={m}>
                    <td colSpan={mostrarVendedor ? 4 : 3} className="px-4 py-2 text-right">
                      Total {m === 'USD' ? 'dólares' : 'pesos'} · {filas.filter((f) => f.p.moneda === m).length}{' '}
                      promesa{filas.filter((f) => f.p.moneda === m).length === 1 ? '' : 's'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">{formatMoney(v, m)}</td>
                    <td />
                  </tr>
                ))}
              </tfoot>
            </table>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerDetalle(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Subgrupos de un aviso: por caja (y moneda), cantidad y monto, el mayor primero. */
function resumenPorCaja(filas: PromesaConDias[]) {
  const m = new Map<string, { medio: string; moneda: 'ARS' | 'USD'; n: number; monto: number }>();
  for (const { p } of filas) {
    const medio = p.medioPago ?? '—';
    const k = `${medio}|${p.moneda}`;
    const c = m.get(k) ?? { medio, moneda: p.moneda, n: 0, monto: 0 };
    c.n += 1;
    c.monto += p.monto;
    m.set(k, c);
  }
  return [...m.values()].sort((a, b) => a.moneda.localeCompare(b.moneda) || b.monto - a.monto);
}

type TonoAlerta = 'rojo' | 'ambar';
type ReportarAlerta = (key: string, n: number, tono: TonoAlerta, etiqueta: string) => void;

/**
 * Avisos de Inicio agrupados: en vez de un banner por aviso (se apilaban y
 * empujaban el tablero), una campana junto a Filtrar con la cantidad en rojo
 * (como Aprobaciones) que abre un panel a la derecha con cada aviso y su
 * "Ver detalle". Se abre aunque no haya nada. Pedido 2026-10-20.
 *
 * Los avisos viven SIEMPRE montados dentro del panel (oculto con CSS, no
 * desmontado): así cada uno corre su query y reporta su cantidad a la campana.
 */
function AlertasHome() {
  const [abierto, setAbierto] = useState(false);
  const [alertas, setAlertas] = useState<Record<string, { n: number; tono: TonoAlerta; etiqueta: string }>>({});
  const reportar = useCallback<ReportarAlerta>((key, n, tono, etiqueta) => {
    setAlertas((prev) =>
      prev[key]?.n === n && prev[key]?.etiqueta === etiqueta ? prev : { ...prev, [key]: { n, tono, etiqueta } },
    );
  }, []);
  // Avisos plegados por el usuario: preferencia de este navegador.
  const [plegadas, setPlegadas] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(CLAVE_PLEGADAS) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const plegar = useCallback((k: string) => {
    setPlegadas((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      try {
        localStorage.setItem(CLAVE_PLEGADAS, JSON.stringify([...next]));
      } catch {
        /* sin storage: el plegado dura lo que la página */
      }
      return next;
    });
  }, []);
  const activas = Object.entries(alertas).filter(([, a]) => a.n > 0);
  // Badge = cantidad de cosas pendientes (como Aprobaciones), no de tipos de aviso.
  const total = activas.reduce((a, [, x]) => a + x.n, 0);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setAbierto(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto]);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="relative h-12 w-12 shrink-0"
        onClick={() => setAbierto(true)}
        aria-label={total > 0 ? `Alertas: ${total}` : 'Alertas'}
        title="Alertas"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {total > 0 && (
          <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground ring-2 ring-background">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </Button>

      {/* Mismo fondo que los Dialog (negro 40% + blur), con fundido: siempre
          montado y animado por opacidad para que entre y salga suave. */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 ease-out',
          abierto ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={() => setAbierto(false)}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label="Alertas"
        aria-hidden={!abierto}
        className={cn(
          'fixed inset-y-0 right-0 z-40 flex w-full max-w-lg flex-col border-l border-border bg-background shadow-2xl transition-[transform,opacity,visibility] duration-300 ease-out',
          abierto ? 'visible translate-x-0 opacity-100' : 'invisible translate-x-8 opacity-0',
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Bell className="h-4 w-4" aria-hidden /> Alertas
          </h2>
          <Button variant="ghost" size="icon" onClick={() => setAbierto(false)} aria-label="Cerrar alertas">
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          {activas.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
              <Bell className="h-8 w-8 opacity-40" aria-hidden />
              No hay alertas para mostrar.
            </div>
          )}
          {/* Los avisos quedan montados aunque estén vacíos o plegados: así
              siguen reportando su cantidad a la campana. */}
          <GrupoAlertas titulo="Tus solicitudes" claves={['resoluciones']} alertas={alertas}>
            <FilaAlerta k="resoluciones" a={alertas.resoluciones} plegada={plegadas.has('resoluciones')} onToggle={plegar}>
              <ResolucionesAlert onCount={reportar} />
            </FilaAlerta>
          </GrupoAlertas>
          <GrupoAlertas titulo="Cobranzas" claves={['vencidas', 'porVencer']} alertas={alertas}>
            <FilaAlerta k="vencidas" a={alertas.vencidas} plegada={plegadas.has('vencidas')} onToggle={plegar}>
              <PromesasCobroAlerts onCount={reportar} mostrar="vencidas" />
            </FilaAlerta>
            <FilaAlerta k="porVencer" a={alertas.porVencer} plegada={plegadas.has('porVencer')} onToggle={plegar}>
              <PromesasCobroAlerts onCount={reportar} mostrar="porVencer" />
            </FilaAlerta>
          </GrupoAlertas>
          <GrupoAlertas titulo="Cheques" claves={['echeqsRebotados', 'echeqsCobrables', 'echeqsProximos']} alertas={alertas}>
            <FilaAlerta k="echeqsRebotados" a={alertas.echeqsRebotados} plegada={plegadas.has('echeqsRebotados')} onToggle={plegar}>
              <EcheqsRebotadosAlert onCount={reportar} />
            </FilaAlerta>
            <FilaAlerta k="echeqsCobrables" a={alertas.echeqsCobrables} plegada={plegadas.has('echeqsCobrables')} onToggle={plegar}>
              <EcheqsAlerts onCount={reportar} mostrar="cobrables" />
            </FilaAlerta>
            <FilaAlerta k="echeqsProximos" a={alertas.echeqsProximos} plegada={plegadas.has('echeqsProximos')} onToggle={plegar}>
              <EcheqsAlerts onCount={reportar} mostrar="proximos" />
            </FilaAlerta>
          </GrupoAlertas>
          <GrupoAlertas titulo="Facturación" claves={['anticipadas', 'cae']} alertas={alertas}>
            <FilaAlerta k="anticipadas" a={alertas.anticipadas} plegada={plegadas.has('anticipadas')} onToggle={plegar}>
              <FacturasAnticipadasAlert onCount={reportar} />
            </FilaAlerta>
            <FilaAlerta k="cae" a={alertas.cae} plegada={plegadas.has('cae')} onToggle={plegar}>
              <CaeHuerfanosAlert onCount={reportar} />
            </FilaAlerta>
          </GrupoAlertas>
        </div>
      </aside>
    </>
  );
}

const CLAVE_PLEGADAS = 'fornitalia.alertas.plegadas';
type EstadoAlerta = { n: number; tono: TonoAlerta; etiqueta: string };

/** Sección del panel ("Cobranzas", "Facturación"): título sólo si alguno de sus avisos tiene algo. */
function GrupoAlertas({
  titulo,
  claves,
  alertas,
  children,
}: {
  titulo: string;
  claves: string[];
  alertas: Record<string, EstadoAlerta>;
  children: React.ReactNode;
}) {
  const n = claves.reduce((a, k) => a + (alertas[k]?.n ?? 0), 0);
  return (
    <section className={cn('space-y-2', n === 0 && 'hidden')}>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{titulo}</h3>
      {children}
    </section>
  );
}

/** Un aviso plegable: encabezado con cantidad; el contenido se oculta pero sigue montado. */
function FilaAlerta({
  k,
  a,
  plegada,
  onToggle,
  children,
}: {
  k: string;
  a: EstadoAlerta | undefined;
  plegada: boolean;
  onToggle: (k: string) => void;
  children: React.ReactNode;
}) {
  const visible = (a?.n ?? 0) > 0;
  return (
    <div className={cn(!visible && 'hidden')}>
      <button
        type="button"
        onClick={() => onToggle(k)}
        aria-expanded={!plegada}
        className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left text-sm hover:bg-muted/60"
      >
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', plegada && '-rotate-90')}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate font-medium first-letter:uppercase">{a?.etiqueta}</span>
        <span
          className={cn(
            'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold',
            a?.tono === 'rojo' ? 'bg-destructive text-destructive-foreground' : 'bg-amber-500 text-white',
          )}
        >
          {a?.n}
        </span>
      </button>
      <div className={cn('pt-1', plegada && 'hidden')}>{children}</div>
    </div>
  );
}
